import moment from "jalali-moment";
import { zeroPad } from "../../lib/zeroPad";
import { DATE_FORMAT_IN_NUMBERS } from "../../constants/dateTime";

moment.locale("fa");

// (string) date format
export const DATE_FORMAT = DATE_FORMAT_IN_NUMBERS;
// (int) The current year
export const THIS_YEAR = +new moment().year();
// (int) The current month starting from 1 - 12
// 1 => January, 12 => December
export const THIS_MONTH = +new moment().month() + 1;
// Week days names and shortnames
export const WEEK_DAYS = {
  Saturday: "ش",
  Sunday: "ی",
  Monday: "د",
  Tuesday: "س",
  Wednesday: "چ",
  Thursday: "پ",
  Friday: "ج"
};
// Calendar months names and short names
export const CALENDAR_MONTHS = {
  Farvardin: "فروردین",
  Ordibehesht: "اردیبهشت",
  khordad: "خرداد",
  Tir: "تیر",
  Mordad: "مرداد",
  Shahrivar: "شهریور",
  Mehr: "مهر",
  Aban: "آبان",
  Azar: "آذر",
  Dey: "دی",
  Bahman: "بهمن",
  Esfand: "اسفند"
};
// Weeks displayed on calendar
export const CALENDAR_WEEKS = 6;

//Check is leap year
export function isLeapYear(year) {
  const matches = [1, 5, 9, 13, 17, 22, 26, 30];
  const modulus = year - Math.floor(year / 33) * 33;
  let isLeapYear = false;

  for (let i = 0; i !== 8; i++) {
    if (matches[i] === modulus) {
      isLeapYear = true;
    }
  }

  return isLeapYear;
}

// (int) Number days in a month for a given year from 28 - 31
export const getMonthDays = (month = THIS_MONTH, year = THIS_YEAR) => {
  const months30 = [7, 8, 9, 10, 11];
  const leapYear = isLeapYear(year);
  return month === 12
    ? leapYear
      ? 30
      : 29
    : months30.includes(month)
    ? 30
    : 31;
};
// (int) First day of the month for a given year from 1 - 7
// 1 => Sunday, 7 => Saturday
export const getMonthFirstDay = (month = THIS_MONTH, year = THIS_YEAR) => {
  return +new moment(`${year}/${zeroPad(month, 2)}/01`).day() + 1;
};

// (bool) Checks if a value is a date - this is just a simple check
export const isDate = date => {
  return date && date.isValid();
};
// (bool) Checks if two date values are of the same year
export const isSameYear = (year, baseYear = new moment().year()) => {
  return +year === +baseYear;
};
// (bool) Checks if two date values are of the same month and year
export const isSameMonth = (
  date,
  baseDate = new moment().format(DATE_FORMAT)
) => {
  if (!(isDate(date) && isDate(baseDate))) return false;
  const _baseDate = moment(baseDate);
  const baseDateMonth = +_baseDate.month() + 1;
  const baseDateYear = _baseDate.year();
  const dateMonth = +date.month() + 1;
  const dateYear = date.year();
  return +baseDateMonth === +dateMonth && +baseDateYear === +dateYear;
};
// (bool) Checks if two date values are the same day
export const isSameDay = (
  date,
  baseDate = new moment().format(DATE_FORMAT)
) => {
  if (!(isDate(date) && isDate(baseDate))) return false;
  const _baseDate = moment(baseDate);
  const baseDateDate = _baseDate.date();
  const baseDateMonth = +_baseDate.month() + 1;
  const baseDateYear = _baseDate.year();
  const dateDate = date.date();
  const dateMonth = +date.month() + 1;
  const dateYear = date.year();
  return (
    +baseDateDate === +dateDate &&
    +baseDateMonth === +dateMonth &&
    +baseDateYear === +dateYear
  );
};
// (string) Formats the given date as YYYY/MM/DD
// Months and Days are zero padded
export const getDateISO = _date => {
  if (!_date) {
    return null;
  }
  const date = moment(_date, DATE_FORMAT);
  if (!isDate(date)) return null;
  return [
    date.year(),
    zeroPad(+date.month() + 1, 2),
    zeroPad(+date.date(), 2)
  ].join("/");
};
// ({month, year}) Gets the month and year before the given month and year
// For example: getPreviousMonth(1, 2000) => {month: 12, year: 1999}
// while: getPreviousMonth(12, 2000) => {month: 11, year: 2000}
export const getPreviousMonth = (month, year) => {
  const prevMonth = month > 1 ? month - 1 : 12;
  const prevMonthYear = month > 1 ? year : year - 1;
  return { month: prevMonth, year: prevMonthYear };
};
// ({month, year}) Gets the month and year after the given month and year
// For example: getNextMonth(1, 2000) => {month: 2, year: 2000}
// while: getNextMonth(12, 2000) => {month: 1, year: 2001}
export const getNextMonth = (month, year) => {
  const nextMonth = month < 12 ? month + 1 : 1;
  const nextMonthYear = month < 12 ? year : year + 1;
  return { month: nextMonth, year: nextMonthYear };
};

// Returns an array of the years in one decade before and one decade after current year.
export const getYearsRange = () => {
  const currentDateYear = moment().year();
  let startYear = currentDateYear - 100;
  startYear = startYear > 0 ? startYear : 0;
  const endYear = currentDateYear + 100;
  return Array(endYear - startYear)
    .fill(startYear)
    .map((x, y) => x + y);
};
// Calendar builder for a month in the specified year
// Returns an array of the calendar dates.
// Each calendar date is represented as an array => [YYYY, MM, DD]
export default (month = THIS_MONTH, year = THIS_YEAR) => {
  // Get number of days in the month and the month's first day

  const monthDays = getMonthDays(month, year);
  const monthFirstDay = getMonthFirstDay(month, year);
  // Get number of days to be displayed from previous and next months
  // These ensure a total of 42 days (6 weeks) displayed on the calendar

  const daysFromPrevMonth = monthFirstDay % 7;
  const daysFromNextMonth =
    CALENDAR_WEEKS * 7 - (daysFromPrevMonth + monthDays);
  // Get the previous and next months and years

  const { month: prevMonth, year: prevMonthYear } = getPreviousMonth(
    month,
    year
  );
  const { month: nextMonth, year: nextMonthYear } = getNextMonth(month, year);
  // Get number of days in previous month
  const prevMonthDays = getMonthDays(prevMonth, prevMonthYear);
  // Build dates to be displayed from previous month

  const prevMonthDates = [...new Array(daysFromPrevMonth)].map((n, index) => {
    const day = index + 1 + (prevMonthDays - daysFromPrevMonth);
    return [prevMonthYear, zeroPad(prevMonth, 2), zeroPad(day, 2)];
  });
  // Build dates to be displayed from current month

  const thisMonthDates = [...new Array(monthDays)].map((n, index) => {
    const day = index + 1;
    return [year, zeroPad(month, 2), zeroPad(day, 2)];
  });
  // Build dates to be displayed from next month

  const nextMonthDates = [...new Array(daysFromNextMonth)].map((n, index) => {
    const day = index + 1;
    return [nextMonthYear, zeroPad(nextMonth, 2), zeroPad(day, 2)];
  });
  // Combines all dates from previous, current and next months
  return [...prevMonthDates, ...thisMonthDates, ...nextMonthDates];
};
